import React, { useMemo, useState } from "react";
import { useAuth } from "../../context/AuthContext";
import {
  compareBowlers,
  nameMatchesSearch,
  sortLabel,
} from "../../utility/bowlerName";
import { buildAverageReviewRow } from "../../utility/applyCatalogToAverages";
import ExtractReview from "../organizations/AverageCatalogs/ExtractReview";
import DesktopTable from "./DesktopTable";
import MobileCards from "./MobileCards";
import ErrorMessage from "./ErrorMessage";
import { LoadingSkeleton } from "./LoadingSkeleton";
import EmptyState from "./EmptyState";
import TableFooter from "./TableFooter";
import AveragesToolbar from "./AveragesToolbar";

const EMPTY_BOWLERS = [];

const matchesStatusFilter = (bowler, statusFilter) => {
  if (statusFilter === "unverified") return !bowler.isVerified;
  if (statusFilter === "verified") return bowler.isVerified;
  if (statusFilter === "no-average") return bowler.hasAverage === false;
  if (statusFilter === "needs-attention") return Boolean(bowler.needsAttention);
  return true;
};

const emptyFilterMessage = (search, statusFilter, remainingCount) => {
  if (statusFilter === "unverified" && !search && remainingCount === 0) {
    return "All averages are verified.";
  }
  if (statusFilter === "needs-attention" && !search) {
    return "No catalog matches need your attention.";
  }
  if (search || statusFilter !== "all") {
    return "No bowlers match this search.";
  }
  return "No bowlers found.";
};

export const BowlerAveragesTableContent = ({
  tournamentBowlers,
  canManage,
  editingBowlerId,
  editedAverage,
  editedHasAverage,
  errorMessage,
  verifyingBowlerId,
  verifyingAll,
  onEditClick,
  onAverageChange,
  onSaveClick,
  onCancelClick,
  onVerifyClick,
  onVerifyAll,
  onHasAverageChange,
  onUseCatalogAverage,
  onReviewCommit,
  canVerifyBowlerForEdit,
  canUnverifyBowlerForEdit,
  catalogInfo,
}) => {
  const { user, loading: authLoading } = useAuth();
  const isLoggedIn = !!user;
  const canManageAndLoggedIn = canManage && isLoggedIn;
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState(null);
  const [sortBy, setSortBy] = useState("lastName");
  const [sortOrder, setSortOrder] = useState("asc");
  const [review, setReview] = useState(null);

  const allBowlers = tournamentBowlers?.bowlerData || EMPTY_BOWLERS;
  const activeFilter =
    statusFilter ?? (canManageAndLoggedIn ? "unverified" : "all");

  const counts = useMemo(
    () => ({
      all: allBowlers.length,
      unverified: allBowlers.filter((bowler) => !bowler.isVerified).length,
      verified: allBowlers.filter((bowler) => bowler.isVerified).length,
      noAverage: allBowlers.filter((bowler) => bowler.hasAverage === false)
        .length,
      needsAttention: allBowlers.filter((bowler) => bowler.needsAttention)
        .length,
    }),
    [allBowlers]
  );

  const visibleBowlers = useMemo(
    () =>
      allBowlers
        .filter(
          (bowler) =>
            nameMatchesSearch(bowler.bowlerName, search) &&
            matchesStatusFilter(bowler, activeFilter)
        )
        .sort((a, b) => compareBowlers(a, b, sortBy, sortOrder)),
    [allBowlers, search, activeFilter, sortBy, sortOrder]
  );

  const handleSortChange = (field) => {
    if (sortBy === field) {
      setSortOrder((current) => (current === "asc" ? "desc" : "asc"));
      return;
    }
    setSortBy(field);
    setSortOrder("asc");
  };

  const remainingCount = counts.unverified;
  const reviewRows = useMemo(
    () =>
      allBowlers
        .filter((bowler) => !bowler.isVerified)
        .sort((left, right) => compareBowlers(left, right, sortBy, sortOrder))
        .map(buildAverageReviewRow),
    [allBowlers, sortBy, sortOrder]
  );

  const openReview = (bowlerId) => {
    const startIndex = Math.max(
      0,
      reviewRows.findIndex((row) => row.bowlerId === bowlerId)
    );
    setReview({ startIndex, rows: reviewRows });
  };

  const visibleTournamentBowlers = {
    ...tournamentBowlers,
    bowlerData: visibleBowlers,
  };
  const emptyMessage = emptyFilterMessage(
    search,
    activeFilter,
    remainingCount
  );

  if (authLoading) {
    return <LoadingSkeleton />;
  }

  if (!allBowlers.length) {
    return <EmptyState />;
  }

  return (
    <div className="card-board overflow-hidden">
      <div className="bg-brand-charcoal text-brand-cream px-6 py-4">
        <h1 className="font-display font-bold text-xl text-brand-cream tracking-tight">
          Averages
        </h1>
        <p className="text-sm text-brand-mist mt-1">
          {sortLabel(sortBy, sortOrder)} Search any part of the name.
          {canManageAndLoggedIn
            ? remainingCount === 0
              ? " All averages are verified."
              : ` ${remainingCount} left to verify.`
            : ""}
          {catalogInfo?.name
            ? ` Using ${catalogInfo.name}.`
            : ""}
        </p>
      </div>

      <AveragesToolbar
        search={search}
        onSearchChange={setSearch}
        statusFilter={activeFilter}
        onStatusFilterChange={setStatusFilter}
        counts={counts}
        sortBy={sortBy}
        sortOrder={sortOrder}
        onSortChange={handleSortChange}
        showNeedsAttention={Boolean(catalogInfo)}
      />

      {errorMessage ? (
        <div className="px-6 pt-4">
          <ErrorMessage message={errorMessage} />
        </div>
      ) : null}

      <DesktopTable
        tournamentBowlers={visibleTournamentBowlers}
        emptyMessage={emptyMessage}
        canManageAndLoggedIn={canManageAndLoggedIn}
        editingBowlerId={editingBowlerId}
        editedAverage={editedAverage}
        editedHasAverage={editedHasAverage}
        errorMessage={errorMessage}
        verifyingBowlerId={verifyingBowlerId}
        verifyingAll={verifyingAll}
        onEditClick={onEditClick}
        onAverageChange={onAverageChange}
        onSaveClick={onSaveClick}
        onCancelClick={onCancelClick}
        onVerifyClick={onVerifyClick}
        onHasAverageChange={onHasAverageChange}
        onUseCatalogAverage={onUseCatalogAverage}
        onReview={openReview}
        canVerifyBowlerForEdit={canVerifyBowlerForEdit}
        canUnverifyBowlerForEdit={canUnverifyBowlerForEdit}
        sortBy={sortBy}
        sortOrder={sortOrder}
        onSortChange={handleSortChange}
      />

      <MobileCards
        tournamentBowlers={visibleTournamentBowlers}
        emptyMessage={emptyMessage}
        canManageAndLoggedIn={canManageAndLoggedIn}
        editingBowlerId={editingBowlerId}
        editedAverage={editedAverage}
        editedHasAverage={editedHasAverage}
        errorMessage={errorMessage}
        verifyingBowlerId={verifyingBowlerId}
        verifyingAll={verifyingAll}
        onEditClick={onEditClick}
        onAverageChange={onAverageChange}
        onSaveClick={onSaveClick}
        onCancelClick={onCancelClick}
        onVerifyClick={onVerifyClick}
        onHasAverageChange={onHasAverageChange}
        onUseCatalogAverage={onUseCatalogAverage}
        onReview={openReview}
        canVerifyBowlerForEdit={canVerifyBowlerForEdit}
        canUnverifyBowlerForEdit={canUnverifyBowlerForEdit}
      />

      <TableFooter
        visibleCount={visibleBowlers.length}
        totalCount={allBowlers.length}
        remainingCount={remainingCount}
        canManageAndLoggedIn={canManageAndLoggedIn}
        verifyingAll={verifyingAll}
        onReviewClick={
          remainingCount > 0
            ? () => openReview(reviewRows[0]?.bowlerId)
            : undefined
        }
      />

      <ExtractReview
        isOpen={Boolean(review)}
        onClose={() => setReview(null)}
        rows={review?.rows || []}
        startIndex={review?.startIndex || 0}
        variant="verify"
        title="Review averages"
        onCommit={onReviewCommit}
      />
    </div>
  );
};
